import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
  Grid,
  Button,
} from "@material-ui/core";

import { Controller, useForm, useWatch } from "react-hook-form";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { Error } from "../../../common/Error";
import React, { useEffect } from "react";
import { Select } from "@ofqual/portal-components/components/Select";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { useLookup } from "@ofqual/portal-core/core/hooks";
import { actionTypesService } from "../../../../services";
import { ActionTypeCode } from "../../../../domain/InformationRequestAction";
import { useDispatch } from "react-redux";
import { postAction } from "../../../../redux/actions";
import { useParams } from "react-router-dom";
import { isFutureDate } from "@ofqual/portal-core/core/utils/dateUtils";

const UPLOAD_DOCS_ACTION_TYPE = 244;
const RESPOND_DOCUMENTS_ACTION_TYPE = 249;

const useStyles = makeStyles((theme) => ({
  actions: {
    padding: 15,
  },
}));

export const CreateActionDialog: React.FC<{
  onClose: () => void;
  isDocuments?: boolean;
  isRespondWithDocuments?: boolean;
}> = ({ onClose, isDocuments, isRespondWithDocuments }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const getActionType = () => {
    if (isDocuments) return UPLOAD_DOCS_ACTION_TYPE;
    if (isRespondWithDocuments) return RESPOND_DOCUMENTS_ACTION_TYPE;
    return null;
  }
  const { handleSubmit, control, errors, setValue } = useForm({
    defaultValues: {
      message: null,
      files: [],
      dueDate: null,
      actionType: getActionType()
    },
  });

  const { id } = useParams<{
    id: string;
  }>();

  const handleCreateAction = (data: {
    files: File[];
    actionType: number;
    message: string;
    dueDate: string;
  }) => {
    dispatch(
      postAction(id, data.files, {
        actionType: data.actionType,
        message: data.message,
        dueDate: data.dueDate,
      })
    );
    onClose();
  };

  const { actionType } = useWatch({
    control,
    name: ["actionType"],
  });

  const [actionTypesLookups] = useLookup(actionTypesService);

  const actionTypeCode = actionTypesLookups.find((x) => x.id === actionType)
    ?.code;

  const uploadDocActionTypeId = actionTypesLookups.find(
    (x) => x.code === ActionTypeCode.UploadDocument
  )?.id;

  const readDocumentsActionTypeId = actionTypesLookups.find(
    x => x.code === ActionTypeCode.ReadDocument
  )?.id;

  useEffect(() => {
    if (isDocuments) {
      setValue("actionType", uploadDocActionTypeId);
    }
    if (isRespondWithDocuments) {
      setValue("actionType", readDocumentsActionTypeId)
    }
  }, [isDocuments, isRespondWithDocuments, setValue, uploadDocActionTypeId, readDocumentsActionTypeId]);

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-test="updated-dialog"
    >
      <DialogTitle data-test="activity-title">Create Action</DialogTitle>
      <Divider />
      <DialogContent>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="actionType"
                rules={{ required: true }}
                as={
                  <Select
                    label="Action Type"
                    datatest="create-action-type-field"
                    required
                    disabled={isDocuments}
                  >
                    {actionTypesLookups}
                  </Select>
                }
              />
              {errors.actionType && <Error>This field is required</Error>}
            </Grid>
            {actionTypeCode === ActionTypeCode.ReadDocument ? (
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="files"
                  rules={{ validate: (value) => !!value.length }}
                  as={
                    <FileInput
                      multiple={true}
                      dataTest="read-attached-select-files-button"
                    />
                  }
                />
                {errors.files && <Error>This field is required</Error>}
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Grid item xs={12}>
                  <Controller
                    control={control}
                    name="message"
                    rules={{ required: true }}
                    render={({ onChange, value }) => (
                      <>
                        <label>Message:</label>
                        <MarkdownEditor
                          onChange={onChange}
                          value={value}
                          dataTest="create-action-message-field"
                        />
                      </>
                    )}
                  />
                </Grid>
                {errors.message && <Error>This field is required</Error>}
              </Grid>
            )}

            <Grid item xs={12}>
              <Controller
                control={control}
                name="dueDate"
                rules={{
                  required: true,
                  validate: (value) => (isFutureDate(value) ? true : false),
                }}
                as={
                  <DatePicker
                    label="Due Date"
                    required
                    dataTest="due-date-field"
                  />
                }
              />
              {errors.dueDate?.type === "required" && (
                <Error>This field is required</Error>
              )}
              {errors.dueDate?.type === "validate" && (
                <Error>Date must be in the future</Error>
              )}
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          color="primary"
          data-test="close-action-dialog"
        >
          Close
        </Button>
        <Button
          onClick={handleSubmit(handleCreateAction)}
          color="primary"
          data-test="confirm-create-action-button"
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
